/**
 * AI Intent Patterns Configuration
 * Defines patterns for detecting user intent and mapping to context types
 * This can be easily extended to support new use cases
 */

export const INTENT_PATTERNS = {
  // Version and changes related
  changes: {
    keywords: ['change', 'recent', 'version', 'update', 'modified', 'diff', 'history', 'deploy', 'deployment', 'previous', 'last'],
    contextTypes: ['versions'],
    description: 'Questions about recent changes, version history, or updates'
  },

  // Usage and performance related
  usage: {
    keywords: ['usage', 'bandwidth', 'performance', 'traffic', 'statistics', 'analytics', 'metrics'],
    contextTypes: ['usage'],
    description: 'Questions about site usage, bandwidth, or performance metrics'
  },

  // Apps and plugins related
  apps: {
    keywords: ['app', 'plugin', 'integration', 'service', 'installed', 'deployed'],
    contextTypes: ['apps'],
    description: 'Questions about installed apps, plugins, or integrations'
  },

  // Deployment related
  deployment: {
    keywords: ['deploy', 'build', 'status', 'compiler', 'auto-deploy', 'deployment'],
    contextTypes: ['deployment'],
    description: 'Questions about deployment status, build process, or deployment configuration'
  },

  // Users and access related
  users: {
    keywords: ['user', 'member', 'access', 'permission', 'team', 'collaborator'],
    contextTypes: ['users'],
    description: 'Questions about site users, permissions, or team access'
  },

  // Forms related
  forms: {
    keywords: ['form', 'submission', 'entry', 'data', 'response'],
    contextTypes: ['forms'],
    description: 'Questions about forms, submissions, or form data'
  },

  // Security related
  security: {
    keywords: ['ssl', 'security', 'https', 'certificate', 'secure'],
    contextTypes: ['security'],
    description: 'Questions about SSL, security settings, or certificates'
  },

  // Custom domain related
  domain: {
    keywords: ['domain', 'custom domain', 'url', 'subdomain'],
    contextTypes: ['domain'],
    description: 'Questions about custom domains or URL configuration'
  }
}

/**
 * Context type definitions
 * Maps context types to their data sources and formatting
 */
export const CONTEXT_TYPES = {
  versions: {
    storeModules: ['versions', 'sites'],
    async: true,
    cacheKey: 'versions',
    description: 'Version history and recent changes'
  },
  
  usage: {
    storeModules: ['sites'],
    async: false,
    cacheKey: 'usage',
    description: 'Bandwidth usage and performance metrics'
  },
  
  apps: {
    storeModules: ['site_app', 'sites'],
    async: true,
    cacheKey: 'apps',
    description: 'Installed apps and plugins'
  },
  
  deployment: {
    storeModules: ['sites'],
    async: false,
    cacheKey: 'deployment',
    description: 'Deployment status and configuration'
  },
  
  users: {
    storeModules: ['site_user', 'user'],
    async: false,
    cacheKey: 'users',
    description: 'Site users and permissions'
  },
  
  forms: {
    storeModules: ['form', 'sites'],
    async: true,
    cacheKey: 'forms',
    description: 'Forms and form submissions'
  },
  
  security: {
    storeModules: ['sites'],
    async: false,
    cacheKey: 'security',
    description: 'SSL and security settings'
  },
  
  domain: {
    storeModules: ['sites'],
    async: false,
    cacheKey: 'domain',
    description: 'Domain and URL configuration'
  }
}

/**
 * Helper function to get all context types for given intents
 * @param {Array} intents - Array of detected intents
 * @returns {Array} Array of context types to load
 */
export function getContextTypesForIntents(intents) {
  const contextTypes = new Set()
  
  intents.forEach(intent => {
    const pattern = INTENT_PATTERNS[intent]
    if (pattern) {
      pattern.contextTypes.forEach(type => contextTypes.add(type))
    }
  })
  
  return Array.from(contextTypes)
}

/**
 * Helper function to get all required store modules for given context types
 * @param {Array} contextTypes - Array of context types
 * @returns {Array} Array of required store modules
 */
export function getStoreModulesForContextTypes(contextTypes) {
  const modules = new Set()
  
  contextTypes.forEach(type => {
    const contextType = CONTEXT_TYPES[type]
    if (contextType) {
      contextType.storeModules.forEach(module => modules.add(module))
    }
  })
  
  return Array.from(modules)
}

export default {
  INTENT_PATTERNS,
  CONTEXT_TYPES,
  getContextTypesForIntents,
  getStoreModulesForContextTypes
} 